import React, { useMemo, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { Popconfirm, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import Icon, {
  ApiOutlined,
  DeleteOutlined,
  EditOutlined,
  GoogleOutlined,
  HistoryOutlined,
  MailOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import BigPandaLogo from '@src/icons/service-integrations/BigPanda.svg';
import DataDogLogo from '@src/icons/service-integrations/DataDog.svg';
import PagerDutyLogo from '@src/icons/service-integrations/PagerDuty.svg';
import { Retry } from '@src/components/common';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import { getTimeZone } from '@src/utils/timezone';
import CreatedBy from '@src/components/common/CreatedBy';
import {
  RuleStatusFilter,
  matchesStatusFilter,
  isForeverUntil,
  type RuleStatusFilterValue,
} from '@src/components/common/RuleStatus';
import { AlertRule, RuleCondition } from '../types';
import { editingRuleAtom, historyRuleAtom } from '../atoms';
import { useDimensionOptions, useMetricOptions } from '../hooks';
import { useFetchAlertRules, useRemoveAlertRule } from './hooks';
import { ConditionTag, GroupLabel, ListToolbar, TableContainer } from '../styled';
import AlertRuleHistory from '../History';

// Brand logos for service-integration channels (reused from the Service
// Integrations screen); email/webhook/Google Chat fall back to antd glyphs
// since they have no dedicated SVG asset.
const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  email: <MailOutlined />,
  webhook: <ApiOutlined />,
  pagerduty: <Icon component={PagerDutyLogo} />,
  datadog: <Icon component={DataDogLogo} />,
  bigpanda: <Icon component={BigPandaLogo} />,
  googlechat: <GoogleOutlined />,
};

const statusColors: Record<string, string> = {
  active: 'green',
  scheduled: 'blue',
  expired: 'default',
};

const formatWindow = (rule: AlertRule) => {
  const tz = getTimeZone() || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  };
  const start = new Date(rule.activeFrom).toLocaleString('en-US', opts);
  const end = isForeverUntil(rule.activeUntil)
    ? 'No end date'
    : new Date(rule.activeUntil).toLocaleString('en-US', opts);
  return `${start} \u2192 ${end}`;
};

const AlertRulesList = ({ isDrawerOpen }: { isDrawerOpen: boolean }) => {
  const isAdminUser = useSelector(isAdminRoleSelector);
  const { data: rules = [], isLoading, isError, refetch } = useFetchAlertRules(isDrawerOpen);
  const { mutate: removeRule } = useRemoveAlertRule();
  const { resolveDimensionLabel } = useDimensionOptions();
  const { resolveMetricLabel } = useMetricOptions(isDrawerOpen);
  const setEditingRule = useSetAtom(editingRuleAtom);
  const setHistoryRule = useSetAtom(historyRuleAtom);

  const [statusFilter, setStatusFilter] = useState<RuleStatusFilterValue>('active_scheduled');

  const filteredRules = useMemo(
    () => rules.filter((r) => matchesStatusFilter(r.status, statusFilter)),
    [rules, statusFilter],
  );

  const renderConditions = (rule: AlertRule) => {
    const conditions = rule.conditions || [];
    const dims = conditions.filter((c: RuleCondition) => c.conditionType === 'dimension');
    const metrics = conditions.filter((c: RuleCondition) => c.conditionType === 'metric');
    return (
      <div>
        {dims.map((c, i) => (
          <ConditionTag key={`d${i}`}>
            {c.matchMode === 'has_dimension'
              ? `Has: ${resolveDimensionLabel(c.dimensionKey || '')}`
              : c.matchMode === 'dimension_contains'
                ? `${resolveDimensionLabel(c.dimensionKey || '')} contains ${c.dimensionLabel || c.dimensionValue}`
                : `${resolveDimensionLabel(c.dimensionKey || '')} = ${c.dimensionLabel || c.dimensionValue}`}
          </ConditionTag>
        ))}
        {dims.length > 0 && metrics.length > 0 ? <GroupLabel>and</GroupLabel> : null}
        {metrics.map((c, i) => (
          <span key={`m${i}`}>
            {i > 0 ? <GroupLabel>or</GroupLabel> : null}
            <ConditionTag>{resolveMetricLabel(c.metricKey || '')}</ConditionTag>
          </span>
        ))}
      </div>
    );
  };

  const columns: ColumnsType<AlertRule> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      ellipsis: true,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Type',
      dataIndex: 'ruleType',
      key: 'ruleType',
      width: 140,
      sorter: (a, b) => (a.ruleType || '').localeCompare(b.ruleType || ''),
      render: (ruleType: string) => (
        <Tag color={ruleType === 'deny' ? 'red' : 'green'}>
          {ruleType === 'deny' ? 'Mute specific' : 'Mute all except'}
        </Tag>
      ),
    },
    {
      title: 'Conditions',
      key: 'conditions',
      width: 300,
      render: (_k, row) => renderConditions(row),
    },
    {
      title: 'Time Window',
      key: 'window',
      width: 200,
      sorter: (a, b) => new Date(a.activeFrom).getTime() - new Date(b.activeFrom).getTime(),
      render: (_k, row) => <div style={{ fontSize: 12 }}>{formatWindow(row)}</div>,
    },
    {
      title: 'Channels',
      key: 'channels',
      width: 140,
      render: (_k, row) => {
        const channels = row.channels || [];
        const byType = channels.reduce((acc: Record<string, number>, c) => {
          acc[c.channelType] = (acc[c.channelType] || 0) + 1;
          return acc;
        }, {});
        return (
          <span>
            {Object.entries(byType).map(([type, count]) => (
              <Tooltip key={type} title={`${count} ${type}`}>
                <span
                  style={{
                    marginRight: 8,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 16,
                  }}
                >
                  {CHANNEL_ICONS[type] || null} {count}
                </span>
              </Tooltip>
            ))}
          </span>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_k, row) => (
        <Tag color={statusColors[row.status]}>
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Created By',
      key: 'createdBy',
      width: 180,
      sorter: (a, b) =>
        (a.createdByName || a.createdByEmail || '').localeCompare(
          b.createdByName || b.createdByEmail || '',
        ),
      render: (_k, row) => (
        <CreatedBy name={row.createdByName} email={row.createdByEmail} timestamp={row.createdAt} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_k, row) => {
        // History is read-only and available to everyone, for every status
        // (incl. expired), so an audit trail is always reachable.
        const historyAction = (
          <Tooltip title="History">
            <a onClick={() => setHistoryRule(row)}>
              <HistoryOutlined />
            </a>
          </Tooltip>
        );

        let editDeleteActions: React.ReactNode = null;
        if (row.status !== 'expired') {
          editDeleteActions = !isAdminUser ? (
            <Tooltip title="Only admins can edit or delete rules">
              <span style={{ display: 'flex', gap: 4, color: '#bfbfbf', cursor: 'not-allowed' }}>
                <EditOutlined />
                <DeleteOutlined />
              </span>
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Edit">
                <a onClick={() => setEditingRule(row)}>
                  <EditOutlined />
                </a>
              </Tooltip>
              <Popconfirm
                placement="left"
                title="Delete this rule?"
                description="It will be detached from all channels and stop applying."
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                okText="Delete"
                cancelText="Cancel"
                onConfirm={() => removeRule(row.id)}
              >
                <Tooltip title="Delete">
                  <a>
                    <DeleteOutlined />
                  </a>
                </Tooltip>
              </Popconfirm>
            </>
          );
        }

        return (
          <span style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            {historyAction}
            {editDeleteActions}
          </span>
        );
      },
    },
  ];

  return (
    <TableContainer>
      <ListToolbar>
        <span style={{ color: '#8c8c8c', fontSize: 13 }}>
          Rules are defined once and attached to one or more channels. Precedence: Global Mute &gt;
          Mute Specific &gt; Mute all except.
        </span>
        <RuleStatusFilter value={statusFilter} onChange={setStatusFilter} />
      </ListToolbar>
      <Retry isLoading={isLoading} fetchData={refetch} showError={isError}>
        <Table
          sticky
          dataSource={filteredRules}
          columns={columns}
          rowKey={(rule) => rule.id}
          showSorterTooltip={false}
          scroll={{ x: true }}
          pagination={false}
          style={{ marginTop: 12 }}
        />
      </Retry>
      <AlertRuleHistory />
    </TableContainer>
  );
};

export default AlertRulesList;
